import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
} from "@angular/core";
import { NovaCard, NovaCardContent } from "@rds-nova/angular-card";
import { NovaIcon } from "@rds-nova/angular-icon";
import { RonMonitorSummary } from "../../models/ron-monitor.models";
import { NO_VALUE } from "../../ron-closing-monitor.utils";

export type SummaryAccent = "info" | "warning" | "neutral";

export interface SummaryCard {
  key: string;
  label: string;
  sublabel: string;
  value: string;
  accent: SummaryAccent;
  iconName: string;
}

function present(value: number | null | undefined): string {
  return value === null || value === undefined ? NO_VALUE : String(value);
}

export function buildSummaryCards(
  summary: RonMonitorSummary | null
): SummaryCard[] {
  const threshold = present(summary?.stuckThresholdMinutes);
  const denominator = present(summary?.completedScheduledDenominator);

  return [
    {
      key: "scheduled",
      label: "Scheduled",
      sublabel: "booked for this day",
      value: present(summary?.scheduled),
      accent: "info",
      iconName: "calendar_month",
    },
    {
      key: "stuck-clients",
      label: "Stuck client",
      sublabel: `≥ ${threshold} min without progress`,
      value: present(summary?.stuckClients),
      accent: "warning",
      iconName: "warning_amber",
    },
    {
      key: "borrower-delayed",
      label: "Borrower delayed",
      sublabel: "borrower running late",
      value: present(summary?.borrowerDelayed),
      accent: "warning",
      iconName: "person",
    },
    {
      key: "notary-delayed",
      label: "Notary delayed",
      sublabel: "notary running late",
      value: present(summary?.notaryDelayed),
      accent: "warning",
      iconName: "headset_mic",
    },
    {
      key: "running-over",
      label: "Running over",
      sublabel: "past scheduled slot",
      value: present(summary?.runningOver),
      accent: "warning",
      iconName: "trending_up",
    },
    {
      key: "completed",
      label: "Completed",
      sublabel: `of ${denominator} scheduled`,
      value: present(summary?.completed),
      accent: "neutral",
      iconName: "check_circle",
    },
    {
      key: "canceled",
      label: "Canceled",
      sublabel: "same-day cancellations",
      value: present(summary?.cancelled),
      accent: "neutral",
      iconName: "cancel",
    },
    {
      key: "open-slots",
      label: "Open slots",
      sublabel: "available for floaters",
      value: present(summary?.openSlots),
      accent: "neutral",
      iconName: "calendar_add",
    },
  ];
}

@Component({
  selector: "app-summary-cards",
  templateUrl: "./summary-cards.component.html",
  styleUrls: ["./summary-cards.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, NovaCard, NovaCardContent, NovaIcon],
  host: {
    class: "rcm-summary",
    role: "group",
    "aria-label": "Closing status summary",
  },
})
export class SummaryCardsComponent implements OnChanges {
  @Input() summary: RonMonitorSummary | null = null;

  cards: SummaryCard[] = buildSummaryCards(null);

  ngOnChanges(): void {
    this.cards = buildSummaryCards(this.summary);
  }

  trackByKey(_index: number, card: SummaryCard): string {
    return card.key;
  }
}
